import React, { useState, useEffect } from 'react';
import { GamingService } from '../services/gaming';

const XPProgressBar = ({ userId, onLevelUp }) => {
  const [progression, setProgression] = useState(null);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpData, setLevelUpData] = useState(null);

  useEffect(() => {
    loadProgression();
    const interval = setInterval(loadProgression, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [userId]);

  const loadProgression = async () => {
    try {
      const data = await GamingService.getProgression();
      
      // Check for level up
      if (progression && data.level > progression.level) {
        const levelUpInfo = {
          oldLevel: progression.level,
          newLevel: data.level,
          title: data.title
        };
        setLevelUpData(levelUpInfo);
        setShowLevelUp(true);
        if (onLevelUp) onLevelUp(levelUpInfo);
      }
      
      setProgression(data);
    } catch (error) {
      console.error('Error loading progression:', error);
    }
  };

  if (!progression) {
    return (
      <div className="w-full bg-gray-900/50 rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-gray-700 rounded"></div>
      </div>
    );
  }

  const progressPercentage = (progression.current_level_xp / progression.next_level_xp) * 100;

  return (
    <>
      <div className="w-full bg-gray-900/50 rounded-lg p-4 border border-gray-700 hover:border-yellow-500/50 transition-all">
        {/* Level and Title */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg font-bold text-xl">
              Level {progression.level}
            </div>
            <div className="text-purple-400 font-bold text-lg">
              {progression.title}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-400">Total XP</div>
            <div className="text-white font-bold">{progression.total_xp.toLocaleString()}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <div className="w-full bg-gray-800 rounded-full h-6 overflow-hidden">
            <div
              className="h-6 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 transition-all duration-500 ease-out flex items-center justify-end px-2"
              style={{ width: `${progressPercentage}%` }}
            >
              <span className="text-xs font-bold text-white drop-shadow-lg">
                {progressPercentage.toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-400">
            <span>{progression.current_level_xp.toLocaleString()} XP</span>
            <span>{progression.next_level_xp.toLocaleString()} XP to next level</span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-3 flex items-center justify-around text-center">
          <div>
            <div className="text-xs text-gray-400">Prestige</div>
            <div className="text-yellow-400 font-bold">{progression.prestige}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Rank</div>
            <div className="text-cyan-400 font-bold text-sm">{progression.current_rank}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Skill Points</div>
            <div className="text-green-400 font-bold">{progression.skill_tree.available_points}</div>
          </div>
        </div>
      </div>

      {/* Level Up Modal */}
      {showLevelUp && levelUpData && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 p-1 rounded-2xl animate-bounce">
            <div className="bg-gray-900 p-8 rounded-2xl text-center">
              <div className="text-6xl font-bold text-yellow-400 mb-4 animate-pulse">
                LEVEL UP!
              </div>
              <div className="text-4xl font-bold text-white mb-2">
                Level {levelUpData.oldLevel} → {levelUpData.newLevel}
              </div>
              {levelUpData.title && (
                <div className="text-2xl text-purple-400 font-bold mb-6">
                  New Title: {levelUpData.title}
                </div>
              )}
              <button
                onClick={() => setShowLevelUp(false)}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg font-bold text-xl hover:scale-105 transition-transform"
              >
                Awesome!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default XPProgressBar;